import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiDownload } from 'react-icons/fi';
import DashboardShell from '../../components/DashboardShell/DashboardShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import WalletBadge from '../../components/WalletBadge/WalletBadge';
import styles from './Rebate.module.css';
import axiosClient from '../../api/axiosClient';
import { useCurrency } from '../../app/CurrencyContext';

interface BreakdownRow {
  level: number;
  type: string;
  amount: number;
  rate: number;
  bonus: number;
  portal: string;
  memberName?: string;
  memberEmail?: string;
  unlockAt?: string | null; matured?: boolean;
  perMonth?: number; monthsEarned?: number; monthsTotal?: number; totalBonus?: number; cancelled?: boolean;
}
interface RebateData {
  directBonus: number; overrideBonus: number; totalBonus: number;
  claimed: number; claimable: number;
  claimableDirect?: number; claimableOverride?: number; overrideMonths?: number;
  overrideLocked?: number; nextOverrideUnlockAt?: string | null;
  switches?: { directBonusClaim: boolean; overrideClaim: boolean; directBonusMessage: string | null; overrideMessage: string | null };
  breakdown: BreakdownRow[];
}

interface MilestoneAward {
  id: string; directName: string; tierLabel: string; pct: number; amountInr: number;
  status: 'claimable' | 'claimed'; onHold: boolean; awardedAt: string; claimedAt: string | null;
}
interface MilestoneData { tiers: { level: number; pct: number; label: string }[]; awards: MilestoneAward[] }

const Rebate: React.FC = () => {
  const { formatFromInr } = useCurrency();
  const [data, setData] = useState<RebateData | null>(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [ms, setMs] = useState<MilestoneData | null>(null);
  const [msBusy, setMsBusy] = useState<string | null>(null);

  const load = () => {
    axiosClient.get('/rebate/mine').then(({ data }) => setData(data)).finally(() => setLoading(false));
    axiosClient.get('/milestone/mine').then(({ data }) => setMs(data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const handleClaim = async () => {
    setClaiming(true);
    try {
      const { data: res } = await axiosClient.post('/rebate/claim');
      toast.success(`Claimed ${formatFromInr(Number(res.claimed))}. It is now in your wallet.`);
      if (Array.isArray(res.skipped) && res.skipped.length) toast.info(res.skipped.join(' '));
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Nothing to claim right now.');
    } finally {
      setClaiming(false);
    }
  };

  const claimMilestone = async (id: string) => {
    setMsBusy(id);
    try {
      const { data: res } = await axiosClient.post(`/milestone/${id}/claim`);
      toast.success(`Claimed ${formatFromInr(Number(res.claimedInr))}. It is now in your wallet.`);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not claim this bonus.');
    } finally {
      setMsBusy(null);
    }
  };

  const breakdown = data?.breakdown || [];

  return (
    <DashboardShell>
      <PageHeader title="Rebate" subtitle="Direct bonus and 5-level team overrides from your referral network." action={<WalletBadge />} />
      <div className={styles.page}>
        {loading && <div className={styles.muted}>Loading…</div>}

        {!loading && data && (
          <>
            <div className={styles.statsRow}>
              <div className={styles.statCard}>
                <div className={styles.statValue}>{formatFromInr(data.directBonus)}</div>
                <div className={styles.statLabel}>Direct bonus</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>{formatFromInr(data.overrideBonus)}</div>
                <div className={styles.statLabel}>Team overrides (5 levels)</div>
              </div>
              <div className={styles.statCard} style={{ borderColor: 'rgba(29,96,246,.4)' }}>
                <div className={styles.statValue} style={{ color: 'var(--copper-light)' }}>{formatFromInr(data.totalBonus)}</div>
                <div className={styles.statLabel}>Total bonus</div>
              </div>
            </div>

            <div className={styles.claimCard}>
              <div>
                <div className={styles.claimLabel}>Already claimed</div>
                <div className={styles.claimValue} style={{ color: 'var(--success)' }}>{formatFromInr(data.claimed)}</div>
              </div>
              <div>
                <div className={styles.claimLabel}>Available to claim</div>
                <div className={styles.claimValue} style={{ color: 'var(--warning)' }}>{formatFromInr(data.claimable)}</div>
              </div>
              <button className={styles.claimBtn} onClick={handleClaim} disabled={claiming || data.claimable <= 0 || (data.switches?.directBonusClaim === false && data.switches?.overrideClaim === false)}>
                <FiDownload /> {claiming ? 'Claiming…' : 'Claim Bonus'}
              </button>
            </div>
            {data.switches && (data.switches.directBonusMessage || data.switches.overrideMessage) && (
              <div className={styles.muted} style={{ marginBottom: 12, color: 'var(--warning)' }}>
                {data.switches.directBonusMessage && <div>{data.switches.directBonusMessage}</div>}
                {data.switches.overrideMessage && <div>{data.switches.overrideMessage}</div>}
              </div>
            )}
            <div className={styles.muted} style={{ marginBottom: 16 }}>
              Direct bonus ({formatFromInr(data.claimableDirect ?? 0)}) is paid once and can be claimed any time.
              Team override is monthly: it appears once a team investment is 30 days old, then pays its rate every month for {data.overrideMonths ?? 12} months,
              and each month can be claimed one month after the previous one ({formatFromInr(data.claimableOverride ?? 0)} ready now).
              {(data.overrideLocked ?? 0) > 0 && (
                <> Still locked: <b>{formatFromInr(data.overrideLocked ?? 0)}</b>
                  {data.nextOverrideUnlockAt ? ` (next unlock ${new Date(data.nextOverrideUnlockAt).toLocaleDateString()})` : ''}.</>
              )}
            </div>

            <h2 className={styles.sectionTitle}>Big investment bonus</h2>
            <div className={styles.muted} style={{ marginBottom: 12 }}>
              When someone you referred directly reaches a total investment of {(ms?.tiers || []).map((t) => `${t.label} (${t.pct}%)`).join(', ') || '₹25 Lac (3%), ₹50 Lac (4%), ₹125 Lac (5%)'},
              you earn that % of the amount. It is paid once per person. If they reach a higher level later, you get only the difference.
            </div>
            {!ms || ms.awards.length === 0 ? (
              <div className={styles.muted} style={{ marginBottom: 24 }}>No big investment bonus yet.</div>
            ) : (
              <table className={styles.table} style={{ marginBottom: 24 }}>
                <thead><tr><th>Direct member</th><th>Level reached</th><th>Rate</th><th>Bonus</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {ms.awards.map((a) => (
                    <tr key={a.id}>
                      <td>{a.directName}</td>
                      <td>{a.tierLabel}</td>
                      <td>{a.pct}%</td>
                      <td>{formatFromInr(a.amountInr)}</td>
                      <td>{a.status === 'claimed' ? 'Claimed' : a.onHold ? 'On hold' : 'Ready to claim'}</td>
                      <td>
                        {a.status === 'claimable' && (
                          <button className={styles.claimBtn} disabled={msBusy === a.id || a.onHold || data?.switches?.directBonusClaim === false} onClick={() => claimMilestone(a.id)}>
                            {msBusy === a.id ? 'Claiming…' : 'Claim'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <h2 className={styles.sectionTitle}>Breakdown</h2>
            {breakdown.length === 0 ? (
              <div className={styles.muted}>No confirmed investments in your team yet — bonuses will appear here once they're confirmed.</div>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr><th>Team Member</th><th>Level</th><th>Type</th><th>Portal</th><th>Invested</th><th>Rate</th><th>Earned so far</th><th>Months</th><th>Next unlock</th></tr>
                </thead>
                <tbody>
                  {breakdown.map((row, i) => (
                    <tr key={i}>
                      <td>
                        <b style={{ display: 'block', fontSize: 12.5 }}>{row.memberName || '—'}</b>
                        <span style={{ fontSize: 10.5, color: 'var(--muted)' }}>{row.memberEmail}</span>
                      </td>
                      <td>{['', '1st team (direct)', '2nd team', '3rd team', '4th team', '5th team'][row.level] || `L${row.level}`}</td>
                      <td>{row.type}</td>
                      <td>Portal {row.portal}</td>
                      <td>{formatFromInr(row.amount)}</td>
                      <td>{(row.rate * 100).toFixed(2)}%</td>
                      <td>{formatFromInr(row.bonus)}</td>
                      <td>{row.monthsTotal !== undefined ? `${row.monthsEarned ?? 0} / ${row.monthsTotal}` : '—'}</td>
                      <td>
                        {row.monthsTotal === undefined
                          ? 'Instant'
                          : row.cancelled ? 'Stopped (cancelled)'
                          : row.unlockAt ? new Date(row.unlockAt).toLocaleDateString() : 'All paid'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </DashboardShell>
  );
};

export default Rebate;